// ═══════════════════════════════════════════════════════════════
// MODAL: Creación de Nueva Nómina
// ═══════════════════════════════════════════════════════════════

import React, { useState } from 'react';
import { X, Calendar, Users, AlertCircle, CheckCircle, Search } from 'lucide-react';

const TIPOS = [
  { value: 'quincenal', label: 'Quincenal' },
  { value: 'mensual',   label: 'Mensual'   },
];

export default function PayrollCreationModal({ onClose, onSubmit, employees }) {
  const [step, setStep]     = useState(1);
  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    tipo:        'quincenal',
    periodo:     new Date().toISOString().slice(0, 7),
    quincena:    'Q1',
    fechaInicio: '',
    fechaFin:    '',
    fechaPago:   '',
    descripcion: ''
  });

  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [selectAll, setSelectAll]                 = useState(false);
  const [searchTerm, setSearchTerm]               = useState('');

  // Auto-calcular fechas
  React.useEffect(() => {
    if (!formData.periodo) return;
    const [year, month] = formData.periodo.split('-');

    if (formData.tipo === 'quincenal') {
      if (formData.quincena === 'Q1') {
        setFormData(prev => ({ ...prev, fechaInicio: `${year}-${month}-01`, fechaFin: `${year}-${month}-15`, fechaPago: `${year}-${month}-15` }));
      } else {
        const lastDay = new Date(year, month, 0).getDate();
        setFormData(prev => ({ ...prev, fechaInicio: `${year}-${month}-16`, fechaFin: `${year}-${month}-${lastDay}`, fechaPago: `${year}-${month}-${lastDay}` }));
      }
    } else if (formData.tipo === 'mensual') {
      const lastDay = new Date(year, month, 0).getDate();
      setFormData(prev => ({ ...prev, fechaInicio: `${year}-${month}-01`, fechaFin: `${year}-${month}-${lastDay}`, fechaPago: `${year}-${month}-${lastDay}` }));
    }
  }, [formData.tipo, formData.periodo, formData.quincena]);

  const activeEmployees    = employees.filter(e => e.estado === 'activo');
  const filteredEmployees  = activeEmployees.filter(emp =>
    `${emp.nombre} ${emp.apellido}`.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.employee_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedEmployees([]);
    } else {
      setSelectedEmployees(filteredEmployees.map(e => e.id));
    }
    setSelectAll(!selectAll);
  };

  const handleToggleEmployee = (empId) => {
    setSelectedEmployees(prev =>
      prev.includes(empId) ? prev.filter(id => id !== empId) : [...prev, empId]
    );
  };

  const validateStep1 = () =>
    formData.tipo && formData.periodo &&
    !(formData.tipo === 'quincenal' && !formData.quincena) &&
    formData.fechaInicio && formData.fechaFin && formData.fechaPago;

  const handleNext = () => {
    if (!validateStep1()) { alert('Por favor completa todos los campos requeridos'); return; }
    setStep(2);
  };

  const handleSubmit = async () => {
    if (selectedEmployees.length === 0) { alert('Debes seleccionar al menos un empleado'); return; }
    setLoading(true);
    try {
      await onSubmit({ ...formData, employeeIds: selectedEmployees });
    } catch (error) {
      alert('Error al crear nómina: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const set = (key, val) => setFormData(prev => ({ ...prev, [key]: val }));

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] flex flex-col overflow-hidden">

        {/* ── Header ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div>
            <h2 className="text-base font-semibold text-gray-900">Nueva Nómina</h2>
            <p className="text-xs text-gray-400 mt-0.5">
              Paso {step} de 2 — {step === 1 ? 'Configuración' : 'Selección de empleados'}
            </p>
          </div>
          <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* ── Progress ────────────────────────────────────────────── */}
        <div className="px-6 pt-3 pb-0">
          <div className="flex items-center gap-2">
            {[1, 2].map(s => (
              <React.Fragment key={s}>
                <div className={`flex items-center gap-1.5 text-xs font-medium ${s <= step ? 'text-blue-600' : 'text-gray-400'}`}>
                  <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold ${
                    s < step ? 'bg-blue-600 text-white' : s === step ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-500'
                  }`}>
                    {s < step ? <CheckCircle className="w-3 h-3" /> : s}
                  </div>
                  {s === 1 ? 'Configuración' : 'Empleados'}
                </div>
                {s < 2 && <div className={`flex-1 h-px ${step > 1 ? 'bg-blue-600' : 'bg-gray-200'}`} />}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* ── Content ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto px-6 py-5 space-y-5">

          {/* STEP 1 */}
          {step === 1 && (
            <>
              {/* Tipo */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Tipo de Nómina *</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                  {TIPOS.map(t => (
                    <button
                      key={t.value}
                      type="button"
                      onClick={() => set('tipo', t.value)}
                      className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                        formData.tipo === t.value
                          ? 'bg-blue-600 text-white border-blue-600'
                          : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {t.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Período */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Período (Mes/Año) *</label>
                <input
                  type="month"
                  value={formData.periodo}
                  onChange={(e) => set('periodo', e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 bg-white"
                />
              </div>

              {/* Quincena */}
              {formData.tipo === 'quincenal' && (
                <div>
                  <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Quincena *</label>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { value: 'Q1', label: 'Primera (1–15)' },
                      { value: 'Q2', label: 'Segunda (16–fin)' },
                    ].map(q => (
                      <button
                        key={q.value}
                        type="button"
                        onClick={() => set('quincena', q.value)}
                        className={`px-3 py-2.5 rounded-lg text-sm font-semibold border transition-all ${
                          formData.quincena === q.value
                            ? 'bg-blue-600 text-white border-blue-600'
                            : 'bg-white text-gray-700 border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }`}
                      >
                        {q.label}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Fechas */}
              <div className="grid grid-cols-3 gap-3">
                {[
                  { key: 'fechaInicio', label: 'Fecha Inicio' },
                  { key: 'fechaFin',    label: 'Fecha Fin'    },
                  { key: 'fechaPago',   label: 'Fecha Pago'   },
                ].map(f => (
                  <div key={f.key}>
                    <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">{f.label} *</label>
                    <input
                      type="date"
                      value={formData[f.key]}
                      onChange={(e) => set(f.key, e.target.value)}
                      className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 bg-white"
                    />
                  </div>
                ))}
              </div>

              {/* Descripción */}
              <div>
                <label className="block text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Descripción</label>
                <textarea
                  value={formData.descripcion}
                  onChange={(e) => set('descripcion', e.target.value)}
                  rows={2}
                  placeholder="Información adicional sobre esta nómina..."
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400 resize-none"
                />
              </div>

              {/* Info */}
              <div className="flex items-start gap-2.5 px-4 py-3 bg-blue-50 border border-blue-100 rounded-lg">
                <AlertCircle className="w-4 h-4 text-blue-500 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-blue-700">
                  Las fechas se calculan automáticamente según el tipo y período seleccionado. Puedes ajustarlas si es necesario.
                </p>
              </div>
            </>
          )}

          {/* STEP 2 */}
          {step === 2 && (
            <>
              {/* Search + select all */}
              <div className="flex items-center gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar empleado..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-9 pr-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:border-blue-400"
                  />
                </div>
                <button
                  onClick={handleSelectAll}
                  className="px-3 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors whitespace-nowrap"
                >
                  {selectAll ? 'Deseleccionar todos' : 'Seleccionar todos'}
                </button>
              </div>

              {/* Counter */}
              <div className="flex items-center justify-between px-3 py-2 bg-gray-50 rounded-lg border border-gray-200">
                <div className="flex items-center gap-2 text-sm">
                  <Users className="w-4 h-4 text-gray-400" />
                  <span className="font-semibold text-gray-900">{selectedEmployees.length}</span>
                  <span className="text-gray-500">seleccionado{selectedEmployees.length !== 1 ? 's' : ''}</span>
                </div>
                <span className="text-xs text-gray-400">{filteredEmployees.length} disponibles</span>
              </div>

              {/* Employee list */}
              <div className="border border-gray-200 rounded-xl overflow-hidden max-h-80 overflow-y-auto">
                {filteredEmployees.length === 0 ? (
                  <div className="p-8 text-center text-sm text-gray-500">No se encontraron empleados</div>
                ) : (
                  filteredEmployees.map((emp, idx) => {
                    const isSelected = selectedEmployees.includes(emp.id);
                    return (
                      <label
                        key={emp.id}
                        className={`flex items-center gap-3 px-4 py-3 cursor-pointer transition-colors ${
                          idx > 0 ? 'border-t border-gray-100' : ''
                        } ${isSelected ? 'bg-blue-50' : 'hover:bg-gray-50'}`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected}
                          onChange={() => handleToggleEmployee(emp.id)}
                          className="w-4 h-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-semibold text-gray-900 truncate">
                            {emp.nombre} {emp.apellido}
                          </p>
                          <p className="text-xs text-gray-400 truncate">
                            {emp.employee_id} · {emp.cargo} · {emp.departamento}
                          </p>
                        </div>
                        <div className="text-right flex-shrink-0">
                          <p className="text-xs font-semibold text-gray-900">
                            RD$ {parseFloat(emp.salario || 0).toLocaleString('es-DO')}
                          </p>
                          <p className="text-[10px] text-gray-400">salario base</p>
                        </div>
                        {isSelected && <CheckCircle className="w-4 h-4 text-blue-500 flex-shrink-0" />}
                      </label>
                    );
                  })
                )}
              </div>
            </>
          )}
        </div>

        {/* ── Footer ──────────────────────────────────────────────── */}
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-gray-50">
          {step === 1 ? (
            <>
              <button
                onClick={onClose}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={handleNext}
                disabled={!validateStep1()}
                className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Siguiente: Empleados →
              </button>
            </>
          ) : (
            <>
              <button
                onClick={() => setStep(1)}
                className="px-4 py-2 text-sm font-medium text-gray-600 border border-gray-200 rounded-lg hover:bg-white transition-colors"
              >
                ← Atrás
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || selectedEmployees.length === 0}
                className="px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                    Creando...
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4" />
                    Crear Nómina
                  </>
                )}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
